import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useInView } from 'framer-motion'
import {
  CheckCircle,
  ChevronDown,
  Users,
  Shield,
  Lock,
  EyeOff,
  FileText,
  Repeat,
  BarChart2,
} from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

const wait = (ms) => new Promise((res) => setTimeout(res, ms))

// ─── Hero Live Ticker ─────────────────────────────────────────────────────────

const tickerLines = [
  'Topic 2 of 4 · on time',
  'New action owned: vendor SLAs · Sarah K.',
  'Time called · moving to Resourcing Gaps',
  "Friday's open items carried into Monday",
]

function LiveTicker() {
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % tickerLines.length), 2800)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-dark-700 shadow-premium">
      <motion.div
        className="w-2 h-2 rounded-full bg-error flex-shrink-0"
        animate={{ opacity: [1, 0.3, 1] }}
        transition={{ duration: 1.4, repeat: Infinity }}
      />
      <span className="text-xs font-semibold text-white">Live</span>
      <span className="w-px h-4 bg-white/20" />
      <div className="relative h-5 w-72 overflow-hidden text-left">
        <AnimatePresence mode="wait">
          <motion.span
            key={idx}
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -12, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 text-xs text-accent-300 font-mono whitespace-nowrap"
          >
            {tickerLines[idx]}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  )
}

// ─── Live Meeting Transition Card ─────────────────────────────────────────────

function LiveMeetingCard() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.6 })
  const [phase, setPhase] = useState(0)

  // phase 0: Topic 1 live and overrunning
  // phase 1: Rithmo fires its announcement
  // phase 2: Topic 2 is now live, Topic 1 marked done
  useEffect(() => {
    if (!isInView) return
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setPhase(0)
        await wait(2800)
        if (cancelled) break
        setPhase(1)
        await wait(1600)
        if (cancelled) break
        setPhase(2)
        await wait(4000)
      }
    }
    run()
    return () => { cancelled = true }
  }, [isInView])

  return (
    <div ref={ref} className="rounded-2xl border border-accent-500/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-dark-600/90">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 rounded-full bg-error"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <span className="text-xs font-semibold text-white">Live · Weekly Planning Sync</span>
        </div>
        <span className="text-[10px] text-dark-200 font-mono">Rithmo Active</span>
      </div>

      <div className="bg-gradient-to-br from-primary-50/60 to-accent-50/40 p-5 space-y-4">

        {/* Current topic + time bar — fixed height, two states overlaid so card never resizes */}
        <div className="relative h-[62px]">
          <motion.div
            animate={{ opacity: phase < 2 ? 1 : 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0"
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-700">Now: Q3 Milestone Review</span>
              <span className="text-xs font-bold text-error">+21 min over</span>
            </div>
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full w-full bg-error rounded-full" />
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">Time limit exceeded: topics 2, 3, 4 not yet reached</p>
          </motion.div>

          <motion.div
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0"
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-700">Now: Resourcing Gaps</span>
              <span className="text-xs font-bold text-success">On track</span>
            </div>
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-primary rounded-full"
                animate={{ width: phase >= 2 ? '13%' : '0%' }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              />
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">2 min elapsed · 15 min planned · topics 3, 4 queued</p>
          </motion.div>
        </div>

        {/* Rithmo announcement — always in DOM, opacity only, so height never changes */}
        <motion.div
          animate={{ opacity: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className="bg-dark-700 rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <img src="/rithmo-mark.png" alt="" className="w-3.5 h-3.5 object-contain flex-shrink-0" />
            <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider">Rithmo · Now</span>
          </div>
          <p className="text-sm text-white leading-snug">
            Time limit reached for Q3 Milestone Review. Moving the team to Topic 2: Resourcing Gaps. 24 minutes remaining.
          </p>
        </motion.div>
      </div>
    </div>
  )
}

// ─── FAQ Accordion ────────────────────────────────────────────────────────────

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="font-semibold text-gray-900">{question}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 ml-4"
        >
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-gray-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Page Data ────────────────────────────────────────────────────────────────

const weekDays = [
  {
    day: 'MON',
    meeting: 'Weekly Ops Sync',
    announce: "Before today's agenda: three actions from Friday are still open. Two have owners. The launch checklist needs one.",
    outcome: 'Nothing lost over the weekend',
  },
  {
    day: 'WED',
    meeting: 'Planning Review',
    announce: 'Time limit reached for Q3 Milestone Review. Moving the team to Topic 2: Resourcing Gaps. 24 minutes remaining.',
    outcome: 'All four topics covered, on time',
  },
  {
    day: 'FRI',
    meeting: 'Cross-Team Review',
    announce: "Before this call ends: five new actions this week, all with owners. Two are due before Monday's sync.",
    outcome: 'Every action leaves with an owner',
  },
]

const faqs = [
  {
    question: 'Does Rithmo replace our current meeting platform?',
    answer: 'No. Rithmo works alongside Zoom, Google Meet, and Microsoft Teams, joining your existing meetings like any other participant. Nothing to migrate, nothing to adopt.',
  },
  {
    question: 'Does Rithmo actually speak in meetings?',
    answer: 'When the team wants it to. Rithmo can host actively: opening with carried-over items, calling time on topics, and reading back actions before the call ends. Teams that prefer a quieter start can run it in observe-only mode first.',
  },
  {
    question: 'Does Rithmo require process changes?',
    answer: 'No. It attaches to the recurring meetings a team already runs and makes them produce more: owners on every action, time discipline on every topic, and context carried into the next session.',
  },
  {
    question: 'Is Rithmo a monitoring tool?',
    answer: 'Rithmo surfaces execution signals: open actions, repeated topics, agenda drift. It is built for operational clarity, not employee surveillance. Transcripts of people are not the product. Movement of work is.',
  },
  {
    question: 'How much work is it to manage?',
    answer: 'An ops leader approves a guardrail once. Rithmo enforces it every session from there. There is no dashboard to babysit and no weekly configuration to maintain.',
  },
  {
    question: 'Who gets the most out of Rithmo?',
    answer: 'Teams that run on recurring meetings: weekly syncs, planning reviews, cross-team standups. The more rhythm an organization has, the more Rithmo compounds.',
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

function OperationsPage() {
  usePageMeta({
    title: 'Rithmo for Operations: Every Meeting Ends in Motion',
    description: 'Rithmo hosts recurring operational meetings: agendas enforced live, every action owned before the call ends, and open items carried forward so nothing repeats.',
  })
  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero (centered) ───────────────────────────────────────────────── */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/50 to-white" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-4">
              <span className="badge-primary text-sm">For Operations Teams</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Every Meeting Ends{' '}
              <span className="gradient-text">in Motion.</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl mx-auto">
              Rithmo hosts your operating rhythm: agendas enforced live, every action owned before the call ends, and nothing repeating next week. Operations gets execution, not transcripts.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/signup" className="btn-primary text-lg px-8 py-4 text-center">
                Get Early Access
              </Link>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <LiveTicker />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── A Week on Rails ───────────────────────────────────────────────── */}
      <section className="pt-8 pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              One Week,{' '}
              <span className="gradient-text">on Rails.</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              This is what a recurring rhythm looks like with Rithmo hosting it. Same meetings, same team, and every session picks up exactly where the last one left off.
            </p>
          </motion.div>

          {/* Vertical week timeline */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-6 md:left-1/2 top-6 bottom-6 w-0.5 bg-gray-200 md:-translate-x-1/2" />

            {weekDays.map((d, i) => (
              <motion.div
                key={d.day}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 }}
                className="relative pb-12 last:pb-0 md:grid md:grid-cols-2 md:gap-16"
              >
                {/* Day node */}
                <div className="absolute left-6 md:left-1/2 top-0 -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-primary text-white flex items-center justify-center text-xs font-bold shadow-glow-sm z-10">
                  {d.day}
                </div>

                {/* Card, alternating sides on desktop */}
                <div className={`pl-16 md:pl-0 ${i % 2 === 1 ? 'md:col-start-2' : 'md:col-start-1'}`}>
                  <div className="card text-left">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{d.meeting}</p>
                    <div className="bg-dark-700 rounded-xl p-4 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <img src="/rithmo-mark.png" alt="" className="w-3.5 h-3.5 object-contain flex-shrink-0" />
                        <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider">Rithmo · To the Room</span>
                      </div>
                      <p className="text-sm text-white leading-snug">{d.announce}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                      <span className="text-sm font-medium text-success">{d.outcome}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center text-gray-500 mt-14 text-lg"
          >
            Then Monday comes,
            <span className="text-gray-900 font-medium"> and nothing starts from zero.</span>
          </motion.p>
        </div>
      </section>

      {/* ── Autonomous Action ─────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Detect the Pattern.{' '}
                <span className="gradient-text">Approve Once.</span>{' '}
                Rithmo Takes the Wheel.
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                When Rithmo spots a recurring problem, one approval puts it to work in the next meeting. When a topic runs over, Rithmo doesn't ask for permission. It calls time, announces the transition to the room, and moves the team forward automatically.
              </p>
              <p className="text-gray-500 leading-relaxed">
                Your ops leader approved the guardrail once. Rithmo enforces it every session from there.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="space-y-3">

              {/* Step 1: Pattern detected */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl border border-error/20 shadow-sm p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-error animate-pulse" />
                  <span className="text-xs font-semibold text-error uppercase tracking-widest">Pattern Detected · Weekly Planning</span>
                </div>
                <p className="text-sm text-gray-800 leading-relaxed mb-3">
                  Topic 1 has run over plan in 5 of the last 6 sessions, averaging 28 extra minutes. Topics 3 and 4 are routinely skipped. Accountability decisions are not being reached.
                </p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    { label: 'Avg overrun', val: '+28 min' },
                    { label: 'Topics skipped', val: '2 of 4' },
                    { label: 'Decisions made', val: '31%' },
                  ].map((m, i) => (
                    <div key={i} className="bg-error/5 border border-error/10 rounded-xl p-2.5 text-center">
                      <p className="text-sm font-bold text-error">{m.val}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{m.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-2 rounded-lg bg-gradient-primary text-white text-xs font-semibold shadow-glow-sm pointer-events-none">Enable Agenda Guardrails</button>
                  <button className="px-4 py-2 rounded-lg border border-gray-200 text-gray-500 text-xs font-medium pointer-events-none">Dismiss</button>
                </div>
              </motion.div>

              <div className="flex justify-center"><div className="w-0.5 h-7 rounded-full bg-gray-300" /></div>

              {/* Step 2: Approved */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-success/5 rounded-2xl border border-success/20 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-800">Ops Director Approved</span>
                  </div>
                  <span className="text-xs text-gray-400">Before next session</span>
                </div>
              </motion.div>

              <div className="flex justify-center"><div className="w-0.5 h-7 rounded-full bg-gray-300" /></div>

              {/* Step 3: Animated live meeting transition */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <LiveMeetingCard />
              </motion.div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Value Bento Grid ──────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-primary-50/30 to-white" />
        <div className="absolute -top-20 right-10 w-72 h-72 bg-accent-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything the Rhythm Needs.{' '}
              <span className="gradient-text">Nothing It Doesn't.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Tile 1: Agendas enforced (large) */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="md:col-span-2 md:row-span-2 card flex flex-col"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Agendas Enforced, Live</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Topics get their time, overruns get called, and skipped items get queued. In the meeting, not in the recap.
              </p>
              <div className="mt-auto space-y-3 bg-gray-50 rounded-2xl p-5">
                {[
                  { topic: 'Q3 Milestone Review', status: 'Done · on time', width: '100%', bar: 'bg-success' },
                  { topic: 'Resourcing Gaps', status: 'Live · 8 min left', width: '55%', bar: 'bg-gradient-primary' },
                  { topic: 'Vendor Decisions', status: 'Queued', width: '0%', bar: 'bg-gray-300' },
                ].map(t => (
                  <div key={t.topic}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{t.topic}</span>
                      <span className="text-xs text-gray-500">{t.status}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${t.bar}`} style={{ width: t.width }} />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tile 2: Owners */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card"
            >
              <Users className="w-6 h-6 text-accent-500 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">An Owner on Every Action</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Open items leave the meeting with a name and a date attached. Rithmo reads them back to the room before the call ends.
              </p>
            </motion.div>

            {/* Tile 3: Repeats flagged */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <Repeat className="w-6 h-6 text-accent-500 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Repeats Get Flagged</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                The third week a topic resurfaces unresolved, the team hears about it, with the history of what was said before.
              </p>
            </motion.div>

            {/* Tile 4: Visibility */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card"
            >
              <BarChart2 className="w-6 h-6 text-accent-500 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Visibility Without Surveillance</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Leaders see how work moves across teams: actions closing, topics resolving, rhythm holding. Signals about execution, never dossiers about people.
              </p>
            </motion.div>

            {/* Tile 5: Observe-only start */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <EyeOff className="w-6 h-6 text-accent-500 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Start as Silent as You Want</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Observe-only mode builds the picture first. Hosting and enforcement turn on when the team is ready, one guardrail at a time.
              </p>
            </motion.div>

            {/* Tile 6: Platforms (wide strip) */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="md:col-span-3 card flex flex-col md:flex-row items-center justify-between gap-6"
            >
              <p className="text-lg text-gray-700 text-center md:text-left">
                <span className="font-semibold text-gray-900">No migration. No new workflow.</span>{' '}
                Rithmo joins the meetings your team already runs.
              </p>
              <div className="flex items-center gap-3">
                {['Zoom', 'Google Meet', 'Microsoft Teams'].map(platform => (
                  <span key={platform} className="px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm font-medium text-gray-700 whitespace-nowrap">{platform}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-4">Security and Trust</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Built to Pass the{' '}
              <span className="gradient-text">Security Review</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Operational intelligence is only valuable when it's trusted. Rithmo is designed to satisfy security and procurement teams, not just operations leaders.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Shield className="w-6 h-6" />,
                title: 'Data Is Never Sold or Shared',
                body: 'Conversation content belongs to your organization. Rithmo does not sell, license, or share meeting data with any third party under any circumstances.',
              },
              {
                icon: <Lock className="w-6 h-6" />,
                title: 'Encrypted at Rest and in Transit',
                body: 'All data is encrypted using AES-256 at rest and TLS 1.3 in transit. Meeting content is never stored as raw recordings.',
              },
              {
                icon: <EyeOff className="w-6 h-6" />,
                title: 'No Recording Retention',
                body: 'Rithmo analyzes meetings for operational signals. Raw audio is processed in-flight and never written to permanent storage.',
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Fully Organization-Scoped',
                body: 'Data is completely isolated to your organization. Nothing is shared across customers or accessible by other teams using Rithmo.',
              },
              {
                icon: <CheckCircle className="w-6 h-6" />,
                title: 'Access Revocable Instantly',
                body: 'Rithmo connects via Zoom OAuth. Any Zoom admin can disconnect access immediately from the admin dashboard without contacting support.',
              },
              {
                icon: <FileText className="w-6 h-6" />,
                title: 'Compliance-Minded by Design',
                body: 'Rithmo is built with SOC 2 principles in mind. Security documentation and data processing agreements are available upon request.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-accent-500/30 hover:shadow-md transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-primary flex items-center justify-center text-white mb-4 shadow-glow-sm">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative glass rounded-3xl p-12 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-primary opacity-5" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-accent-500/20 rounded-full blur-3xl" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Put Your Operating Rhythm on Rails
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
                Rithmo is onboarding early operations teams now. Setup is light, and the first meeting is the demo.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/signup" className="btn-primary text-lg px-8 py-4">
                  Get Early Access
                </Link>
                <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                  Talk to the Team
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card divide-y divide-gray-100"
          >
            {faqs.map(faq => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default OperationsPage
